import { yupResolver } from '@hookform/resolvers/yup'
import { EFinancingProgram } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import { ControlledNumberField, ControlledTextField } from '@if/mui'
import {
  Alert,
  Box,
  Button,
  CardContent,
  CardHeader,
  debounce,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import AsyncActionButton from '@src/components/AsyncActionButton'
import PageSpinner from '@src/components/PageSpinner'
import { CardBorderTop } from '@src/components/common/Card'
import { useComputeProductsLoan } from '@src/data/api/calculator-api/calculator-api'
import { FinancingConfigDto, useMerchantFinancingConfig } from '@src/data/api/configs-api/financing-config-api'
import { useProductsProviders } from '@src/data/api/products-api/products-api'
import { usePostProductsWorksheet, usePutProductsWorksheet } from '@src/data/api/worksheet-api/product-worksheet-api'
import { isValidFirstPayment } from '@src/data/worksheet-selectors'
import useFeatureFlags from '@src/hooks/config-hooks'
import { reportErrorToConsole } from '@src/services/error-logger'
import { ETermsList, FinancingProgramConfigs } from '@src/types/Constants'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { ProductsComputeRequestDto } from '@src/types/ProductsComputeRequestDto'
import { buildProductWorksheetSchema, ProductsWorksheet } from '@src/types/WorksheetSchema'
import { JSX, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import ProductsLoanFundingSummary from './Components/ProductsFundindSummary'
import ProductsInsurances from './Components/ProductsInsurances'
import ProductsSplitApplicationPrompt from './Components/ProductsSplitApplicationPrompt'
import ProductsWorksheetDetails from './Components/ProductsWorksheetDetails'

interface Props {
  merchant: Merchant
  creditApplication: CreditApplication
  merchantPayments: { label: string; value: string; id: string }[]
}

interface FormProps extends Props {
  financingConfig: FinancingConfigDto | undefined
  productsCapEnabled: boolean
}

const ProductsWorksheetForm = ({ merchant, creditApplication, financingConfig, productsCapEnabled }: FormProps) => {
  const ProductWorksheetSchema = useMemo(() => {
    return buildProductWorksheetSchema(undefined, { financingConfig, featureEnabled: productsCapEnabled })
  }, [financingConfig, productsCapEnabled])

  const [defaultValues] = useState<ProductsWorksheet>(
    (creditApplication.worksheet ?? ProductWorksheetSchema.getDefault()) as unknown as ProductsWorksheet,
  )

  const {
    control,
    setValue,
    watch,
    getValues,
    handleSubmit,
    trigger,
    reset,
    formState: { errors, isDirty },
  } = useForm<ProductsWorksheet>({
    mode: 'onBlur',
    defaultValues,
    resolver: yupResolver(ProductWorksheetSchema),
  })

  const vehicleFinancingTerm = watch('vehicleFinancingTerm')
  const vehicleSellingAmount = watch('vehicleSellingAmount')
  useEffect(() => {
    void trigger(['creditInsurance', 'extendedWarranty', 'replacementOrGapInsurance'])
  }, [trigger, vehicleFinancingTerm, vehicleSellingAmount])

  const [postWorksheet, isPosting, isPostError, postError] = usePostProductsWorksheet()

  const [putWorksheet, isPuting, isPutError, updateError] = usePutProductsWorksheet()

  const [fundingData, setFundingData] = useState<ProductsComputeRequestDto | undefined>(undefined)

  const [computedFunding, isComputing] = useComputeProductsLoan(fundingData)

  const [productsProviders, isLoadingProductProviders] = useProductsProviders()

  const navigate = useNavigate()
  const { t } = useTranslation()

  const navigateToView = () => {
    navigate(`/credits/${creditApplication.financingProgramId}/${creditApplication.id}`, { replace: true })
  }

  const debouncedSetFundingData = useMemo(
    () => debounce((data: ProductsComputeRequestDto) => setFundingData(data), 1000),
    [],
  )

  const onSubmit = async (formData: ProductsWorksheet) => {
    formData.creditApplicationId = creditApplication.id
    if (creditApplication.worksheet == null) {
      await postWorksheet({ worksheet: formData }).then(() => {
        navigateToView()
      })
    } else {
      await putWorksheet({
        worksheet: formData,
      }).then(() => {
        navigateToView()
      })
    }
  }

  const [creditInsuranceTerm, replacementOrGapInsuranceTerm, extendedWarrantyTerm] = watch([
    'creditInsurance.term',
    'replacementOrGapInsurance.term',
    'extendedWarranty.term',
  ])

  const maxTermDuration = useMemo(() => {
    const decisionMaxTerm = creditApplication.finalCreditDecision.maxTermDuration ?? 0

    const productTerms = [creditInsuranceTerm, replacementOrGapInsuranceTerm, extendedWarrantyTerm]
    if (productTerms.length === 0) return decisionMaxTerm
    const highestProductTerm = Math.max(...productTerms.filter((t) => t !== null && t !== undefined))
    return Math.min(highestProductTerm, decisionMaxTerm)
  }, [
    creditApplication.finalCreditDecision.maxTermDuration,
    creditInsuranceTerm,
    extendedWarrantyTerm,
    replacementOrGapInsuranceTerm,
  ])

  const [amountRequested, paymentFrequency, term, deliveryOn, firstPaymentOn] = watch([
    'amountRequested',
    'paymentFrequency',
    'term',
    'deliveryOn',
    'firstPaymentOn',
  ])

  useEffect(() => {
    // @ts-expect-error Yup typing doesn't make complete sense. The term needs to be unset. Here
    if (term > maxTermDuration) setValue('term', null)
  }, [term, maxTermDuration, setValue])

  const prevAmountRequested = useRef(amountRequested)
  useEffect(() => {
    if (prevAmountRequested.current === amountRequested) return
    prevAmountRequested.current = amountRequested
    if (isDirty) void trigger('amountRequested')
  }, [amountRequested, trigger, isDirty])

  useEffect(() => {
    if (
      [amountRequested, paymentFrequency, term, deliveryOn, firstPaymentOn].every((x) => !!x) &&
      (amountRequested ?? 0) > 0 &&
      isValidFirstPayment(deliveryOn, firstPaymentOn)
    ) {
      const calculationData = {
        stateIso: creditApplication.applicant.currentAddress.stateIso,
        amountRequested,
        frequency: paymentFrequency,
        term,
        deliveryOn,
        firstPaymentDate: formatDate(firstPaymentOn),
      } as ProductsComputeRequestDto

      debouncedSetFundingData(calculationData)

      return () => {
        debouncedSetFundingData.clear()
      }
    }
  }, [
    creditApplication.applicant.currentAddress.stateIso,
    amountRequested,
    paymentFrequency,
    term,
    deliveryOn,
    firstPaymentOn,
    debouncedSetFundingData,
  ])

  const loanTermChoicesList = useMemo<JSX.Element[]>(
    () =>
      ETermsList.filter((termChoice) => Number(termChoice) <= maxTermDuration).map((item) => (
        <MenuItem key={item} value={item}>{`${item} ${t('common.months')}`}</MenuItem>
      )) || [],
    [maxTermDuration, t],
  )

  const [financedProductsWithTermShorterThanLoanTerm, shouldProposeToSplitApplication] = useMemo(() => {
    const financedProducts: { name: string; term: number }[] = [
      { name: t('productsWorksheet.replacementInsurance'), term: replacementOrGapInsuranceTerm ?? 0 },
      { name: t('productsWorksheet.creditInsurance'), term: creditInsuranceTerm ?? 0 },
      { name: t('productsWorksheet.extendedWarranty'), term: extendedWarrantyTerm ?? 0 },
    ].filter((x) => x.term !== 0)

    const financedProductsWithTermShorterThanLoanTerm = financedProducts.filter((x) => x.term < term)

    const shouldProposeToSplitApplication =
      financedProducts.length > 1 && financedProductsWithTermShorterThanLoanTerm.length !== 0

    return [financedProductsWithTermShorterThanLoanTerm, shouldProposeToSplitApplication]
  }, [t, replacementOrGapInsuranceTerm, creditInsuranceTerm, extendedWarrantyTerm, term])

  const [splitApplicationId, setSplitApplicationId] = useState<string | null>(null)

  if (isLoadingProductProviders) return <PageSpinner withBackdrop isLoading />

  const openNewTab = (url: string) => {
    window.open(url)
  }

  return (
    <div>
      {(postError || updateError) && (
        <Paper>
          <Alert severity="error">
            <Typography>
              {postError?.response?.data?.message ||
                updateError?.response?.data?.message ||
                t('common.errors.serverError')}
            </Typography>
          </Alert>
        </Paper>
      )}
      <form onSubmit={handleSubmit(onSubmit, reportErrorToConsole)}>
        <Stack direction={{ md: 'column', xs: 'column-reverse' }} spacing={5} justifyContent="space-between">
          <Stack
            direction={{ sm: 'row', xs: 'column' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ sm: 'self-end', xs: 'self-start' }}
          >
            <Stack direction="column" justifyContent="space-between" spacing={2}>
              <Typography variant="h4">{t('worksheet.worksheet')}</Typography>
            </Stack>
            <Stack direction="row" spacing={2} display="flex">
              <AsyncActionButton fullWidth={false} variant="contained" href="..">
                {t('common.previous')}
              </AsyncActionButton>

              <AsyncActionButton
                fullWidth={false}
                variant="contained"
                disabled={isComputing || !computedFunding}
                isPending={isPosting || isPuting}
                isError={isPostError || isPutError}
                onClick={handleSubmit(onSubmit)}
              >
                {t('worksheet.createContract')}
              </AsyncActionButton>
            </Stack>
          </Stack>

          <Stack direction={{ md: 'row', xs: 'column-reverse' }} spacing={3} justifyContent="space-between">
            <Box sx={{ flex: 1 }}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Grid container spacing={2}>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6,
                        md: 4,
                      }}
                    >
                      <ControlledTextField
                        control={control}
                        name="vehicleFinancingTerm"
                        label={t('productsWorksheet.vehicleFinancingTerm')}
                        slotProps={{
                          input: {
                            endAdornment: <InputAdornment position="end">{t('common.month')}</InputAdornment>,
                          },
                        }}
                      />
                    </Grid>
                    {productsCapEnabled && (
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                          md: 4,
                        }}
                      >
                        <ControlledNumberField
                          control={control}
                          name="vehicleSellingAmount"
                          label={t('productsWorksheet.vehicleSellingAmount')}
                          fullWidth
                          isMoney
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>

                <Grid size={12}>
                  <Typography variant="body1" component={Alert} severity="info">
                    {t('productsWorksheet.optional', {
                      amount: FinancingProgramConfigs[EFinancingProgram.Products].minLoanAmount,
                    })}
                    <Divider />
                    {t('productsWorksheet.maxTerm', {
                      maxTermDuration: creditApplication.finalCreditDecision.maxTermDuration,
                    })}
                  </Typography>
                </Grid>

                <Grid size={12}>
                  <ProductsInsurances
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    getValues={getValues}
                    trigger={trigger}
                    stateIso={creditApplication.applicant.currentAddress.stateIso!}
                    insurancesProvider={productsProviders.filter((x) => merchant.insuranceProviderIds.includes(x.id))}
                    warrantyProvider={productsProviders.filter((x) => merchant.warrantyProviderIds.includes(x.id))}
                    hasContractEndDate={defaultValues?.replacementOrGapInsurance?.endDate !== undefined}
                  />
                </Grid>

                <Grid size={12}>
                  <CardBorderTop color="secondary">
                    <CardHeader title={t('common.financing')} />
                    <CardContent>
                      <ProductsWorksheetDetails
                        disabledAmountInput
                        control={control}
                        creditApplication={creditApplication}
                        maxTermDuration={maxTermDuration}
                        loanTermChoicesList={loanTermChoicesList}
                      />
                    </CardContent>
                  </CardBorderTop>
                </Grid>
              </Grid>
            </Box>

            <Stack gap={2}>
              {shouldProposeToSplitApplication ? (
                <ProductsSplitApplicationPrompt
                  financingTerm={term}
                  financedProductsWithTermShorterThanLoanTerm={financedProductsWithTermShorterThanLoanTerm}
                  creditApplicationId={creditApplication.id}
                  unvalidatedWorksheet={getValues()}
                  onNewApplicationCreated={(newAppId, updatedWorksheetData) => {
                    setSplitApplicationId(newAppId)
                    reset(updatedWorksheetData, { keepDefaultValues: false })
                    setValue('versionTag', creditApplication.worksheet?.versionTag ?? '')
                  }}
                />
              ) : (
                <ProductsLoanFundingSummary computedFunding={computedFunding} isFetching={isComputing} />
              )}

              {splitApplicationId !== null && (
                <CardBorderTop sx={{ width: '25rem' }}>
                  <CardContent>
                    <Typography sx={{ pb: 2 }}>
                      {t('productsWorksheet.splitApplicationPrompt.productsHaveBeenMovedToANewApplication')}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => {
                        openNewTab(`/credits/products/${splitApplicationId}/worksheet`)
                      }}
                    >
                      {t('productsWorksheet.splitApplicationPrompt.viewNewApplication')}
                    </Button>
                  </CardContent>
                </CardBorderTop>
              )}
            </Stack>
          </Stack>
        </Stack>
      </form>
    </div>
  )
}

const ProductsWorksheetPage = (props: Props) => {
  const { flags } = useFeatureFlags()
  const productsCapEnabled = !!flags.UseProductChargebackOrRetentionConfig
  const [financingConfig, isFetchingConfig] = useMerchantFinancingConfig(props.merchant.id, EFinancingProgram.Products)

  if (productsCapEnabled && isFetchingConfig && !financingConfig) return <PageSpinner withBackdrop isLoading />

  return (
    <ProductsWorksheetForm {...props} financingConfig={financingConfig} productsCapEnabled={productsCapEnabled} />
  )
}

export default ProductsWorksheetPage
